/**
 * Was this error response Claro's own answer, or something in front of it?
 *
 * CLA-1541 item 5. Cloudflare sits in front of every Claro host, and its block
 * and challenge pages are 403s. The web clients used to end the session on any
 * 400/401/403 from the refresh call, so an edge or WAF page signed the
 * clinician (or patient) out exactly as if Claro had refused the refresh
 * token, and a clinician signed out through an edge block cannot sign back in
 * through the same block (CLA-1389).
 *
 * Claro's answer (claro-elixir): `application/json`, a JSON object with a
 * string `error`, `detail` or `code`:
 * - `AuthPageController.session_refresh` and `PatientAuthController.refresh`
 *   400/401: `{"detail"}`
 * - `ClaroWeb.Plugs.Auth` 401 and `ClaroWeb.ErrorJSON`: `{"error","code","detail"}`
 * - the legal and verified-email gates' 403: `{"error", ...}`
 *
 * Not Claro's, whatever the status:
 * - any body that is not `application/json` (Cloudflare's HTML pages);
 * - a response carrying `cf-mitigated` (Cloudflare's challenge marker);
 * - Cloudflare's structured JSON error (served to `Accept: application/json`,
 *   RFC 9457 style), which has a `detail` too but also `cloudflare_error` and
 *   `ray_id`, keys Claro never sends;
 * - an empty or unparseable body.
 *
 * `cf-ray` alone proves nothing: every response through Cloudflare carries it,
 * Claro's own included.
 *
 * The same rule as Android `ClaroRefusal.isFromClaro` (claro-android #137) and
 * iOS `Claro/ClaroRefusal.swift` (CLA-1538). Change them together.
 */

const CLARO_ERROR_KEYS = ['error', 'detail', 'code'] as const
const CLOUDFLARE_ERROR_KEYS = ['cloudflare_error', 'ray_id'] as const

/**
 * `application/json`, parameters (`; charset=utf-8`) ignored. Exactly that
 * type: `application/problem+json` is not what Claro sends.
 */
export function isJsonMediaType(contentType: string | null | undefined): boolean {
  if (!contentType) return false
  const essence = contentType.split(';')[0].trim().toLowerCase()
  return essence === 'application/json'
}

/** The body half on its own. */
export function isClaroErrorBody(body: string | null | undefined): boolean {
  if (!body || !body.trim()) return false
  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    return false
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return false
  const obj = parsed as Record<string, unknown>
  if (CLOUDFLARE_ERROR_KEYS.some((k) => k in obj)) return false
  return CLARO_ERROR_KEYS.some((k) => typeof obj[k] === 'string')
}

/** The full test, from the parts of a response. */
export function isFromClaroParts(parts: {
  contentType: string | null
  cfMitigated: string | null
  body: string | null
}): boolean {
  if (parts.cfMitigated !== null) return false
  if (!isJsonMediaType(parts.contentType)) return false
  return isClaroErrorBody(parts.body)
}

/**
 * The full test for a fetch `Response`. Reads (and so consumes) the body:
 * call it once, on a response nothing else will read.
 */
export async function isFromClaro(res: Response): Promise<boolean> {
  const cfMitigated = res.headers.get('cf-mitigated')
  const contentType = res.headers.get('content-type')
  // Skip reading a body the headers already rule out.
  if (cfMitigated !== null || !isJsonMediaType(contentType)) return false
  let body: string | null
  try {
    body = await res.text()
  } catch {
    body = null
  }
  return isFromClaroParts({ contentType, cfMitigated, body })
}
